import { Select } from "../input-functions";

export type DateTimeFilterValue =
  | ""
  | "last_30_minutes"
  | "last_1_hour"
  | "last_6_hours"
  | "last_24_hours"
  | "last_7_days"
  | "last_30_days"
  | "custom";

export type DateTimeRange = {
  filter: DateTimeFilterValue;
  from: string;
  to: string;
};

const dateTimeFilterOptions: Array<{ value: DateTimeFilterValue; label: string }> = [
  { value: "", label: "All" },
  { value: "last_30_minutes", label: "Last 30 Minutes" },
  { value: "last_1_hour", label: "Last 1 Hour" },
  { value: "last_6_hours", label: "Last 6 Hours" },
  { value: "last_24_hours", label: "Last 24 Hours" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "custom", label: "Custom" },
];

function getRelativeFrom(filter: DateTimeFilterValue): string | undefined {
  const now = Date.now();
  const hour = 60 * 60 * 1000;
  const day = 24 * hour;
  const ranges: Partial<Record<DateTimeFilterValue, number>> = {
    last_30_minutes: 30 * 60 * 1000,
    last_1_hour: hour,
    last_6_hours: 6 * hour,
    last_24_hours: day,
    last_7_days: 7 * day,
    last_30_days: 30 * day,
  };
  const range = ranges[filter];
  return range ? new Date(now - range).toISOString() : undefined;
}

function localDateTimeToIso(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function getDateTimeRangeQuery(range: DateTimeRange): {
  evaluatedAtFrom?: string;
  evaluatedAtTo?: string;
} {
  if (range.filter === "custom") {
    return {
      evaluatedAtFrom: localDateTimeToIso(range.from),
      evaluatedAtTo: localDateTimeToIso(range.to),
    };
  }

  const evaluatedAtFrom = getRelativeFrom(range.filter);
  if (!evaluatedAtFrom) return {};

  return {
    evaluatedAtFrom,
  };
}

type DateTimeFilterProps = {
  label: string;
  value: DateTimeRange;
  onChange: (value: DateTimeRange) => void;
};

export default function DateTimeFilter({ label, value, onChange }: DateTimeFilterProps) {
  return (
    <>
      <div className="col-12 col-md-6 col-lg-3">
        <label className="form-label">{label}</label>
        <Select
          value={value.filter}
          onChange={(e) => onChange({ ...value, filter: e.target.value as DateTimeFilterValue })}
          options={dateTimeFilterOptions}
        />
      </div>
      {value.filter === "custom" && (
        <>
          <div className="col-12 col-md-6 col-lg-3">
            <label className="form-label">From</label>
            <input
              type="datetime-local"
              className="form-control"
              value={value.from}
              max={value.to || undefined}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
            />
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <label className="form-label">To</label>
            <input
              type="datetime-local"
              className="form-control"
              value={value.to}
              min={value.from || undefined}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
            />
          </div>
        </>
      )}
    </>
  );
}
